/**
 * @file Menu 导航菜单
 * @description 垂直 / 水平导航；支持分组、子菜单展开、选中态；可接路由 `to`。
 *
 * @whenToUse 侧栏导航、顶栏水平菜单、页内导航列表。
 * @whenNotToUse
 * 1. 按钮触发的操作浮层（用 DropdownMenu）。
 * 2. 表单枚举选择（用 Select / Segmented）。
 */

import { useState, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Collapse } from '@/components/ui/collapse.tsx'

export type MenuItem = {
  /** 唯一键 */
  key: string
  /** 文案 */
  label: ReactNode
  /** 前置图标 */
  icon?: ReactNode
  /** 禁用 */
  disabled?: boolean
  /** 危险样式 */
  danger?: boolean
  /** 路由路径（有则用 NavLink，激活态由路由决定） */
  to?: string
  /** 点击回调（无 to 时） */
  onSelect?: () => void
  /** 子项；`type: 'group'` 时作分组，否则为可折叠子菜单 */
  children?: MenuItem[]
  /**
   * item：普通项 / 子菜单
   * group：分组标题 + 子项（标题不可点）
   */
  type?: 'item' | 'group'
}

export type MenuProps = {
  items: MenuItem[]
  /** 布局方向 */
  mode?: 'vertical' | 'horizontal'
  /** 受控选中（无 to 的项） */
  selectedKeys?: string[]
  /** 选中变化 */
  onSelect?: (key: string, item: MenuItem) => void
  /** 受控展开的子菜单 key */
  openKeys?: string[]
  /** 默认展开 */
  defaultOpenKeys?: string[]
  /** 展开变化 */
  onOpenChange?: (keys: string[]) => void
  /**
   * 受控：菜单是否收起（垂直模式仅图标）
   * 传入则为受控；否则用 `defaultCollapsed` 非受控
   */
  collapsed?: boolean
  /** 非受控：初始是否收起 */
  defaultCollapsed?: boolean
  /** 收起状态变化 */
  onCollapsedChange?: (collapsed: boolean) => void
  /** 垂直模式显示内置收起/展开按钮 */
  collapsible?: boolean
  className?: string
}

export function Menu({
  items,
  mode = 'vertical',
  selectedKeys = [],
  onSelect,
  openKeys,
  defaultOpenKeys = [],
  onOpenChange,
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onCollapsedChange,
  collapsible = false,
  className,
}: MenuProps) {
  const [innerOpen, setInnerOpen] = useState(defaultOpenKeys)
  const [innerCollapsed, setInnerCollapsed] = useState(defaultCollapsed)
  const opened = openKeys ?? innerOpen
  const collapsed = collapsedProp ?? innerCollapsed

  const setOpened = (keys: string[]) => {
    onOpenChange?.(keys)
    if (openKeys === undefined) setInnerOpen(keys)
  }

  /**
   * 更新收起状态
   * @param next - 是否收起
   */
  const setCollapsed = (next: boolean) => {
    onCollapsedChange?.(next)
    if (collapsedProp === undefined) setInnerCollapsed(next)
  }

  const toggleOpen = (key: string) => {
    if (collapsed) return
    setOpened(
      opened.includes(key)
        ? opened.filter((k) => k !== key)
        : [...opened, key],
    )
  }

  const handleSelect = (item: MenuItem) => {
    if (item.disabled) return
    item.onSelect?.()
    onSelect?.(item.key, item)
  }

  const menuList = (
    <ul
      role="menu"
      data-collapsed={collapsed || undefined}
      className={cn(
        mode === 'horizontal'
          ? 'flex flex-row flex-wrap items-center gap-1'
          : 'flex flex-col gap-1 transition-[width] duration-300',
        mode === 'vertical' && collapsed && 'w-10 items-stretch',
        !collapsible && className,
      )}
    >
      {items.map((item) => (
        <MenuNode
          key={item.key}
          item={item}
          mode={mode}
          depth={0}
          collapsed={collapsed}
          selectedKeys={selectedKeys}
          opened={collapsed ? [] : opened}
          onToggleOpen={toggleOpen}
          onSelectItem={handleSelect}
        />
      ))}
    </ul>
  )

  if (!collapsible || mode !== 'vertical') {
    return menuList
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <button
        type="button"
        aria-label={collapsed ? '展开菜单' : '收起菜单'}
        aria-expanded={!collapsed}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-lg border border-transparent px-2 text-sm text-muted transition-colors duration-200 hover:bg-accent/25',
          collapsed && 'w-10 justify-center',
        )}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? (
          <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
        ) : (
          <>
            <PanelLeftClose className="size-4 shrink-0" aria-hidden />
            <span>收起</span>
          </>
        )}
      </button>
      {menuList}
    </div>
  )
}

type MenuNodeProps = {
  item: MenuItem
  mode: 'vertical' | 'horizontal'
  depth: number
  collapsed: boolean
  selectedKeys: string[]
  opened: string[]
  onToggleOpen: (key: string) => void
  onSelectItem: (item: MenuItem) => void
}

function MenuNode({
  item,
  mode,
  depth,
  collapsed,
  selectedKeys,
  opened,
  onToggleOpen,
  onSelectItem,
}: MenuNodeProps) {
  const isGroup = item.type === 'group'
  const isSubmenu = Boolean(item.children?.length) && !isGroup
  const isOpen = opened.includes(item.key)

  if (isGroup && item.children) {
    return (
      <li role="none" className={cn(mode === 'horizontal' && 'contents')}>
        {!collapsed ? (
          <div
            className={cn(
              'px-2 py-1.5 text-xs font-medium text-muted whitespace-nowrap',
              mode === 'horizontal' && 'px-2',
            )}
          >
            {item.label}
          </div>
        ) : null}
        <ul
          role="group"
          className={cn(
            mode === 'horizontal'
              ? 'flex flex-row flex-wrap items-center gap-1'
              : 'flex flex-col gap-1',
          )}
        >
          {item.children.map((child) => (
            <MenuNode
              key={child.key}
              item={child}
              mode={mode}
              depth={depth + 1}
              collapsed={collapsed}
              selectedKeys={selectedKeys}
              opened={opened}
              onToggleOpen={onToggleOpen}
              onSelectItem={onSelectItem}
            />
          ))}
        </ul>
      </li>
    )
  }

  if (isSubmenu && item.children) {
    return (
      <li role="none">
        <button
          type="button"
          role="menuitem"
          aria-expanded={isOpen}
          disabled={item.disabled}
          title={typeof item.label === 'string' ? item.label : undefined}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-lg border border-transparent px-2 text-sm text-muted transition-colors duration-200 hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50',
            collapsed && 'justify-center',
            mode === 'horizontal' && 'w-auto',
          )}
          style={
            !collapsed && mode === 'vertical' && depth > 0
              ? { paddingLeft: 8 + depth * 12 }
              : undefined
          }
          onClick={() => onToggleOpen(item.key)}
        >
          {item.icon ? (
            <span className="shrink-0 [&_svg]:size-4">{item.icon}</span>
          ) : null}
          {!collapsed ? (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  isOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </>
          ) : null}
        </button>
        {!collapsed ? (
          <Collapse open={isOpen}>
            <ul role="menu" className="mt-1 flex flex-col gap-1">
              {item.children.map((child) => (
                <MenuNode
                  key={child.key}
                  item={child}
                  mode={mode}
                  depth={depth + 1}
                  collapsed={collapsed}
                  selectedKeys={selectedKeys}
                  opened={opened}
                  onToggleOpen={onToggleOpen}
                  onSelectItem={onSelectItem}
                />
              ))}
            </ul>
          </Collapse>
        ) : null}
      </li>
    )
  }

  return (
    <li role="none">
      <MenuLeaf
        item={item}
        mode={mode}
        depth={depth}
        collapsed={collapsed}
        selectedKeys={selectedKeys}
        onSelectItem={onSelectItem}
      />
    </li>
  )
}

function MenuLeaf({
  item,
  mode,
  depth,
  collapsed,
  selectedKeys,
  onSelectItem,
}: {
  item: MenuItem
  mode: 'vertical' | 'horizontal'
  depth: number
  collapsed: boolean
  selectedKeys: string[]
  onSelectItem: (item: MenuItem) => void
}) {
  const selected = selectedKeys.includes(item.key)
  const padStyle =
    !collapsed && mode === 'vertical' && depth > 0
      ? { paddingLeft: 8 + depth * 12 }
      : undefined

  const className = ({ isActive }: { isActive?: boolean } = {}) =>
    cn(
      'flex h-9 items-center gap-2 rounded-lg border border-transparent px-2 text-sm transition-colors duration-200',
      mode === 'vertical' ? 'w-full' : 'w-auto',
      collapsed && 'justify-center',
      item.disabled && 'cursor-not-allowed opacity-50',
      item.danger && !isActive && !selected && 'text-danger',
      isActive || selected
        ? 'bg-primary/10 font-medium text-primary'
        : 'text-muted hover:bg-primary/10 hover:text-primary',
    )

  if (item.to) {
    return (
      <NavLink
        to={item.to}
        end={item.to === '/'}
        role="menuitem"
        title={typeof item.label === 'string' ? item.label : undefined}
        aria-disabled={item.disabled || undefined}
        className={({ isActive }) => className({ isActive })}
        style={padStyle}
        onClick={(event) => {
          if (item.disabled) {
            event.preventDefault()
            return
          }
          onSelectItem(item)
        }}
      >
        <MenuLeafContent item={item} collapsed={collapsed} />
      </NavLink>
    )
  }

  return (
    <button
      type="button"
      role="menuitem"
      disabled={item.disabled}
      title={typeof item.label === 'string' ? item.label : undefined}
      className={className({ isActive: selected })}
      style={padStyle}
      onClick={() => onSelectItem(item)}
    >
      <MenuLeafContent item={item} collapsed={collapsed} />
    </button>
  )
}

function MenuLeafContent({
  item,
  collapsed,
}: {
  item: MenuItem
  collapsed: boolean
}) {
  return (
    <>
      {item.icon ? (
        <span className="shrink-0 [&_svg]:size-4">{item.icon}</span>
      ) : null}
      {!collapsed ? (
        <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
      ) : null}
    </>
  )
}
